import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink, ExternalLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from '../ToolboxPage/ToolboxPage.module.css';

const SITE = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://www.bigloads.com';

const TITLE = 'Trailer Rental Insurance for Owners | BigLoads';
const DESC =
  'Renting out your trailer? Your personal auto or homeowners policy usually will not cover a trailer in a renter\u2019s hands. Learn what coverage you actually need and get a quote built for trailer rental businesses \u2014 all 50 states.';

// ---- Partner outbound (UTM-tagged so TrailerGuard can see the traffic we send) ----
// Jordan's call (2026-09-26): send visitors to the MAIN SITE homepage so they can see the
// whole business first, not straight to the form. (The direct form is at
// https://portal.trailerguardinsurance.com/get-started if we ever want to deep-link it.)
const PARTNER_BASE = 'https://trailerguardinsurance.com/';
const utm = medium =>
  `${PARTNER_BASE}?utm_source=bigloads&utm_medium=${medium}&utm_campaign=owner_insurance`;
const PARTNER_QUOTE = utm('insurance_hub');

// ---- FAQ (also emitted as FAQPage JSON-LD) ----
const FAQ = [
  {
    q: 'Do I need special insurance to rent out my trailer?',
    a:
      'In most cases, yes. A standard personal auto or homeowners policy is written for personal use, not for a trailer that earns money in someone else\u2019s hands. Most personal policies exclude \u201Cbusiness use,\u201D which is exactly what renting is. If a renter damages the trailer or causes an accident while towing it, a personal policy will often deny the claim. A commercial or rental-specific policy is built to cover that gap.',
  },
  {
    q: 'What kind of coverage should a trailer rental business carry?',
    a:
      'Two coverages do the heavy lifting: general liability, which is premises liability for where you store your trailers, so if a renter is injured at your property you have defense costs if you get sued, and it keeps you compliant with any lease you have with your landlord. The other is physical damage, which covers your trailers for fire, theft, and vandalism. Together they cover the two things that actually go wrong in a rental operation.',
  },
  {
    q: 'Are there security requirements I have to meet for physical damage coverage?',
    a:
      'Yes, and they are simple. All trailers must have a tongue lock while they are sitting in your yard. If a renter unhooks a trailer from the tow vehicle, it has to be locked up while it is not attached to that vehicle. These are basic anti-theft steps insurers expect, and staying on top of them protects both your coverage and your equipment.',
  },
  {
    q: 'Am I covered while a renter has my trailer?',
    a:
      'Not under a typical personal policy. That is the single biggest gap owners get caught by. A rental-specific policy is designed so coverage follows the trailer, meaning liability and physical-damage protection stay in force while a unit is in a customer\u2019s hands. That is the whole point of insuring for rental use.',
  },
  {
    q: 'Does insurance replace a signed rental agreement?',
    a:
      'No, and neither replaces the other. Insurance covers the money side when something goes wrong; a signed rental agreement sets the rules, deposit, and damage terms up front and is what you rely on to make a claim or recover a deposit. Serious owners use both. You can grab a free, state-aware rental agreement in the Owner Toolbox.',
  },
  {
    q: 'How much does trailer rental insurance cost?',
    a:
      'It depends on your fleet size, trailer values, where you operate, and your rental volume, so there is no single number. The honest answer is to get a quote. A rental-focused insurer can usually turn one around quickly once they know your fleet, and there is typically no minimum fleet size, so it works whether you rent one trailer on weekends or run a full lot.',
  },
];

const Shield = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const InsurancePageComponent = ({ scrollingDisabled, currentUser }) => {
  const canonical = `${SITE}/trailer-rental-insurance`;

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Trailer Rental Insurance', item: canonical },
    ],
  };
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQ.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const articleSchema = {
    '@type': 'Article',
    headline: 'Trailer Rental Insurance for Owners',
    description: DESC,
    abstract:
      'Renting out a trailer is a business, and personal auto or homeowners policies usually exclude business use, so they will not cover a trailer while a renter has it. Owners need general liability plus physical damage coverage written for rental use, so protection follows the trailer on the road and on the lot.',
    image: [`${SITE}/static/og/bigloads-og.jpg`],
    author: { '@type': 'Organization', name: 'BigLoads' },
    publisher: { '@type': 'Organization', name: 'BigLoads' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
  };

  // Auth-aware link: the Owner Toolbox pages are login-gated (auth + ownerOnly).
  // A logged-out visitor (or Googlebot) must NOT be dropped onto a 401/blank page,
  // so when there is no current user we route toolbox CTAs into the free owner
  // signup funnel (with `from` so they bounce back after signing up). Logged-in
  // owners get the real page.
  const isLoggedIn = !!(currentUser && currentUser.id);
  const ownerLink = (toolboxRouteName, backPath, anchor, extraClass) => {
    if (isLoggedIn) {
      return (
        <NamedLink name={toolboxRouteName} className={extraClass}>
          {anchor}
        </NamedLink>
      );
    }
    return (
      <NamedLink name="SignupPage" to={{ state: { from: backPath } }} className={extraClass}>
        {anchor}
      </NamedLink>
    );
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={TITLE}
      description={DESC}
      schema={[breadcrumbSchema, articleSchema, faqSchema]}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        {/* ---- hero ---- */}
        <section className={css.hero}>
          <div className={css.wrap}>
            <div className={css.eyebrow}>Owner Resource</div>
            <h1 className={css.h1}>Trailer Rental Insurance, Built for Owners</h1>
            <p className={css.lede}>
              If you rent out your trailer, your personal auto or homeowners policy probably will
              not cover it once a renter takes the keys. Here is the coverage that actually protects
              your equipment and your income, plus a way to get a quote made for exactly what you do.
            </p>
            <div className={css.freePill}>
              <Shield /> Coverage available in all 50 states
            </div>
          </div>
        </section>

        {/* ---- body ---- */}
        <section className={css.guideMain}>
          <div className={css.wrap}>
            <div className={css.crumbs}>
              <NamedLink name="LandingPage">Home</NamedLink> &nbsp;/&nbsp; Trailer Rental Insurance
            </div>

            <div className={css.guideBody}>
              <div className={css.keyBand}>
                <div className={css.kbLabel}>The short version</div>
                <p>
                  <strong>Renting out a trailer is a business, and personal policies exclude
                  business use.</strong> That means a standard auto or homeowners policy will often
                  deny a claim if a renter damages your trailer or causes an accident while towing
                  it. Rental-specific coverage closes that gap so protection follows your trailer,
                  on your lot and on the road.
                </p>
              </div>

              <h2>Why your personal policy is not enough</h2>
              <p>
                Most owners assume the coverage they already carry will protect a trailer they rent
                out. It usually will not. Personal auto and homeowners policies are written for
                personal use, and nearly all of them exclude business or commercial use. The moment
                your trailer starts earning money in someone else&rsquo;s hands, you are outside what
                that policy was designed to cover.
              </p>
              <p>
                The result is the gap that catches owners off guard: a renter jackknifes your dump
                trailer, a strap fails and someone&rsquo;s cargo is destroyed, or a unit is stolen off
                a job site, and the personal insurer points to the business-use exclusion and walks
                away. You are left covering the repair, the liability, or the full replacement out of
                pocket, which can wipe out a year of rental income in a single incident.
              </p>

              <h2>The two coverages a trailer rental business needs</h2>
              <div className={css.steps}>
                <div className={css.step}>
                  <div className={css.stepNum}>1</div>
                  <div className={css.stepBody}>
                    <h4>General Liability</h4>
                    <p>
                      This is premises liability for where you store your trailers. If a renter is
                      injured at your property, general liability provides your defense costs if you
                      get sued. It also keeps you compliant with any lease agreement you have in
                      place with your landlord, which usually requires it. This is the coverage that
                      stands between an accident on your lot and a lawsuit aimed at you personally.
                    </p>
                  </div>
                </div>
                <div className={css.step}>
                  <div className={css.stepNum}>2</div>
                  <div className={css.stepBody}>
                    <h4>Physical Damage</h4>
                    <p>
                      Covers your actual trailer against fire, theft, and vandalism, whether it is
                      sitting on your lot or halfway across the state behind a renter&rsquo;s truck.
                      This is what gets a damaged unit repaired and back to earning instead of
                      parked. To keep this coverage in force, insurers expect basic anti-theft steps,
                      covered just below.
                    </p>
                  </div>
                </div>
              </div>
              <p>
                Together those two cover the two things that actually go wrong in a rental operation:
                someone gets hurt on your property, or the trailer gets damaged. A rental-focused
                policy also tends to scale with you, so you can add or retire units as your fleet
                changes without over-paying for trailers you no longer run.
              </p>

              <div className={css.tipBox}>
                <b>Keep your trailers locked to keep your coverage.</b> Physical damage policies come
                with a couple of common-sense security requirements. All trailers must have a tongue
                lock while they are sitting in your yard, and if a renter unhooks a trailer from the
                tow vehicle, it has to be locked up while it is not attached to that vehicle. These
                simple habits protect both your equipment and your claim.
              </div>

              <div className={css.tipBox}>
                <b>Insurance and a signed contract work together.</b> Coverage handles the money
                when something goes wrong. A signed rental agreement sets the rules, deposit, and
                damage terms up front and is what you lean on to make a claim or keep a deposit. Grab
                a free, state-aware one in our{' '}
                {ownerLink('ToolboxRentalAgreementPage', '/toolbox/rental-agreement', 'Rental Agreement toolkit')}.
              </div>

              <h2>Where to get coverage built for trailer rentals</h2>
              <p>
                Most general insurance agents do not understand trailer rental exposure, so they
                either decline the risk or write a policy that leaves the same gaps. We looked for a
                partner that insures trailer rental businesses specifically, and points owners toward{' '}
                <ExternalLink href={utm('insurance_hub_inline')}>TrailerGuard</ExternalLink>, a team
                with decades of insurance experience now focused on exactly this: general liability
                and physical damage protection built around rental fleets, in all 50 states, with no
                minimum fleet size.
              </p>
              <p>
                It is a fit for how BigLoads owners already operate. Their program is built for
                owners who run a real rental business: trailers 20 years or newer, clean titles,
                renters 18 or older, and a signed rental agreement on every rental. If that already
                sounds like how you rent, you are most of the way there.
              </p>

              <div className={css.ctaBand}>
                <h3>Get a quote built for your fleet</h3>
                <p>
                  Tell them about your trailers and how you rent. Most quotes come back within about
                  one business day, and there is no payment info required up front.
                </p>
                <div className={css.ctaBtns}>
                  <ExternalLink href={PARTNER_QUOTE} className={css.btnOrange}>
                    Get an Insurance Quote
                  </ExternalLink>
                  {ownerLink('ToolboxIndexPage', '/toolbox', 'Owner Toolbox', css.btnOutline)}
                </div>
              </div>

              <h2>Frequently asked questions</h2>
              {FAQ.map(f => (
                <div key={f.q}>
                  <h3>{f.q}</h3>
                  <p>{f.a}</p>
                </div>
              ))}

              <h2>Keep building a rental business that protects itself</h2>
              <p>
                Insurance is one layer. The habits around it matter just as much: a signed agreement,
                dated photos before and after every rental, and a clear plan for damage. Our{' '}
                {ownerLink('ToolboxIndexPage', '/toolbox', 'Owner Toolbox')} has the free templates
                and checklists for all of it, and if you want more units earning, you can{' '}
                <NamedLink name="PresellPage">list your trailer on BigLoads</NamedLink> in a few
                minutes.
              </p>

              <div className={css.shield}>
                <b>Please note:</b> BigLoads (Micawber Media LLC) is not an insurance agency, broker,
                or producer, and does not sell, negotiate, or give insurance advice. This page is a
                general educational resource for trailer owners. TrailerGuard is an independent third
                party; BigLoads is not paid a commission for referrals and does not guarantee any
                coverage, quote, rate, or claim outcome. Coverage, eligibility, and pricing are
                determined solely by the insurer. Always confirm the specifics of any policy directly
                with the insurer before you rely on it. See our{' '}
                <NamedLink name="TermsOfServicePage">Terms of Service</NamedLink> for the full terms
                that apply.
              </div>
            </div>
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

InsurancePageComponent.defaultProps = { currentUser: null };

InsurancePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: object,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  currentUser: state.user.currentUser,
});

const InsurancePage = compose(connect(mapStateToProps))(InsurancePageComponent);

export default InsurancePage;
